// packages/web/src/components/service/ServiceWhatToExpect.tsx
// 4-step vertical timeline — steps auto-selected by service category. Seller never fills this in.

import { Box, Divider, Typography } from '@mui/material';
import React from 'react';

import type { MarketplaceService } from '../../types/pricing';

interface JourneyStep {
  icon: string;
  title: string;
  desc: string;
}

function getServiceJourney(service: MarketplaceService): JourneyStep[] {
  const billingMode = ((service as any).billing_mode || 'partner_bills') as string;
  const payStep: JourneyStep =
    billingMode === 'aithon_bills'
      ? {
          icon: '📋',
          title: 'Complete your booking',
          desc: "Payment is collected now. You'll receive an instant receipt and your order details through Aithon.",
        }
      : {
          icon: '📋',
          title: 'Place your order',
          desc: 'No payment collected yet — we confirm your details and availability first.',
        };

  const fulfillmentType = ((service as any).fulfillment_type || '').toLowerCase();
  const cat = ((service as any).category || '').toLowerCase();
  const svcType = ((service as any).service_type || '').toLowerCase();

  // ── 1. fulfillment_type is the most reliable discriminator ──────────────

  // By-invitation / fit-check gate (Fractional AI Officer pattern)
  if (cat.includes('fractional') || cat.includes('officer') || (cat.includes('invitation'))) {
    return [
      payStep,
      {
        icon: '🤝',
        title: 'Fit confirmation',
        desc: "If it's a match, the provider confirms through Aithon and your card is charged. If it's not the right fit, the hold is released immediately — nothing owed.",
      },
      {
        icon: '⚙️',
        title: 'Onboarding begins',
        desc: 'Your provider reaches out to schedule your kickoff. The engagement takes shape in week one.',
      },
      {
        icon: '🚀',
        title: "You're operational",
        desc: 'Ongoing service, optimization, and support — month to month.',
      },
    ];
  }

  // Field installation — fiber, dedicated circuits, physical installs
  if (
    fulfillmentType === 'field_installation_service' ||
    cat.includes('fiber') ||
    cat.includes('ethernet') ||
    cat.includes('dfi') ||
    cat.includes('dedicated') ||
    cat.includes('onsite') ||
    cat.includes('warranty')
  ) {
    return [
      payStep,
      {
        icon: '🗓',
        title: 'Site survey scheduled',
        desc: 'A technician contacts you within 3–5 business days to schedule a free site survey.',
      },
      {
        icon: '🔧',
        title: 'Installation',
        desc: 'Typical installation takes 30–90 days depending on infrastructure availability at your location.',
      },
      {
        icon: '🚀',
        title: "You're live",
        desc: 'Service activates and billing begins on your first invoice.',
      },
    ];
  }

  // Virtual appointment / one-time consulting (non-subscription professional services)
  if (
    fulfillmentType === 'virtual_appointment_service' ||
    (cat.includes('consulting') && svcType !== 'month_to_month') ||
    cat.includes('audit') ||
    cat.includes('support')
  ) {
    return [
      payStep,
      {
        icon: '📬',
        title: 'Provider reaches out within 72 hours',
        desc: "You'll get a message through Aithon with next steps — a scheduling link, kickoff call invite, or direct contact. If you don't hear back, Aithon will check in with you.",
      },
      {
        icon: '⚙️',
        title: 'Work begins',
        desc: 'The provider delivers the service per the scope and timeline in your order. Track status and message directly through your Aithon dashboard.',
      },
      {
        icon: '✅',
        title: 'Delivery confirmed',
        desc: 'You confirm the work is complete. Funds are released to the provider. Order history stays in your Aithon account.',
      },
    ];
  }

  // Subscription / managed services
  if (
    fulfillmentType === 'subscription_service' ||
    svcType === 'month_to_month' ||
    cat.includes('managed') ||
    cat.includes('managed_services') ||
    cat.includes('retainer')
  ) {
    return [
      payStep,
      {
        icon: '🤝',
        title: 'Kickoff call and scope confirmation',
        desc: "After the online order, your provider confirms the environment, package assumptions, and implementation specifics with you. This is the human confirmation step, but the order starts online instead of with a cold sales chase.",
      },
      {
        icon: '📝',
        title: 'Paperwork and onboarding',
        desc: 'Contract documents are finalized online, then the provider begins onboarding, transition planning, and setup for the service you selected.',
      },
      {
        icon: '🚀',
        title: "Installation, then billing",
        desc: 'The provider completes onboarding and activates the service. For partner-billed offers, billing typically begins after contract execution and implementation, not at the initial cart step.',
      },
    ];
  }

  // Digital delivery — cloud, SaaS, software, security licenses
  if (
    fulfillmentType === 'digital_delivery' ||
    cat.includes('cloud') ||
    cat.includes('security') ||
    cat.includes('software') ||
    cat.includes('saas')
  ) {
    return [
      payStep,
      {
        icon: '⚙️',
        title: 'Account provisioned',
        desc: 'Your environment is configured and credentials are delivered — typically within 1–3 business days.',
      },
      {
        icon: '🎓',
        title: 'Onboarding',
        desc: 'Your consultant walks your team through the platform. Most onboarding sessions are under an hour.',
      },
      {
        icon: '🚀',
        title: "You're live",
        desc: 'Your team is up and running from day one.',
      },
    ];
  }

  // Voice, SIP trunking, UC, collaboration
  if (
    cat.includes('voice') ||
    cat.includes('sip') ||
    cat.includes('trunking') ||
    cat.includes('unified') ||
    cat.includes('calling') ||
    cat.includes('collaboration') ||
    cat.includes('webex') ||
    cat.includes('ringcentral') ||
    cat.includes('uc')
  ) {
    return [
      payStep,
      {
        icon: '📞',
        title: 'Number porting begins',
        desc: 'Your existing numbers are ported to the new service — typically 7–14 business days.',
      },
      {
        icon: '⚙️',
        title: 'Configuration & testing',
        desc: 'Your consultant configures call routing, auto-attendants, and voicemail.',
      },
      {
        icon: '🚀',
        title: "You're live",
        desc: 'Service goes live seamlessly — zero downtime during the cutover window.',
      },
    ];
  }

  // Connectivity — internet, broadband, cable, TV, general carrier services
  if (
    cat.includes('connectivity') ||
    cat.includes('internet') ||
    cat.includes('broadband') ||
    cat.includes('cable') ||
    cat.includes('tv') ||
    cat.includes('media')
  ) {
    return [
      payStep,
      {
        icon: '📦',
        title: 'Equipment shipped',
        desc: 'Router and modem shipped to your location — typically arrives in 3–5 business days.',
      },
      {
        icon: '🔧',
        title: 'Tech visit or self-install',
        desc: 'A technician activates the line at your location, or you self-install with our guide.',
      },
      {
        icon: '🚀',
        title: "You're live",
        desc: 'Service is active and billing begins on your first invoice.',
      },
    ];
  }

  // Default — generic fallback
  return [
    payStep,
    {
      icon: '🤝',
      title: 'Confirm the specifics',
      desc: 'Your provider reaches out through Aithon to confirm implementation details, timeline, and any service-specific assumptions tied to the online order.',
    },
    {
      icon: '📝',
      title: 'Paperwork and scheduling',
      desc: 'Any required agreements, implementation paperwork, or scheduling steps are completed online before the provider starts fulfillment.',
    },
    {
      icon: '✅',
      title: 'Fulfillment and billing',
      desc: 'The service proceeds through normal delivery and, for partner-billed offers, billing typically begins after contract execution or installation rather than at the first cart step.',
    },
  ];
}

const ServiceWhatToExpect: React.FC<{ service: MarketplaceService }> = ({ service }) => {
  const steps = getServiceJourney(service);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        What to expect
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {steps.map((step, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 2, position: 'relative' }}>
            {/* Step connector line */}
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  backgroundColor: '#eff6ff',
                  border: '2px solid #bfdbfe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1rem',
                  zIndex: 1,
                }}
              >
                {step.icon}
              </Box>
              {/* Connector line between steps */}
              {i < steps.length - 1 && (
                <Box
                  sx={{ width: 2, flex: 1, backgroundColor: '#e2e8f0', my: 0.5, minHeight: 16 }}
                />
              )}
            </Box>
            {/* Step content */}
            <Box sx={{ pb: i < steps.length - 1 ? 2 : 0, pt: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {step.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {step.desc}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default React.memo(ServiceWhatToExpect);
